import {DataTable} from "../DataTable/DataTable";
import {TransportLineItemEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {ActionIcon, Group, Text} from "@mantine/core";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {useTransportLineItemDataTableFilter} from "./useTransportLineItemDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {IconCircleDashed, IconReportMoney} from "@tabler/icons-react";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency";
import {useCostedDataTable} from "../DataTable/useCostedDataTable";
import {CostDetailsModal} from "../../modal/CostDetailsModal/CostDetailsModal";
import {useState} from "react";
import {TliDescriptionCell} from "./TliDescriptionCell/TliDescriptionCell";
import {CodingIcon} from "../InvoiceDataTable/InvoiceDataTable";

const EMPTY_ARRAY: TransportLineItemEntity[] = []

export type TransportLineItemDataTableProps = EntityDataTableProps<TransportLineItemEntity>

export const TransportLineItemDataTable = (props: TransportLineItemDataTableProps) => {
  const {
    records: tlis = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props;

  const [selectedRecord, setSelectedRecord] = useState<TransportLineItemEntity | undefined>(undefined)

  const {t} = useTranslation(["common"]);

  const costedTableProps = useCostedDataTable({
    records: tlis,
  })

  const {filteredRecords, filterConfig} = useTransportLineItemDataTableFilter({
    records: tlis
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'transportlineitemNumber', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <>
      <DataTable
        {...rest}
        {...(pagination ?? {})}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        columns={[
          {
            title: '',
            accessor: "seqNum",
            width: 25,
            sortable: true,
            render: (record) => (
              <Group spacing={4} position="left" noWrap>
                {record.disputed &&
                  <Text color="red">
                    <IconCircleDashed size={14}/>
                  </Text>
                }
                <CodingIcon
                  coded={record.coding ? true : undefined}
                  codes={record.code ? [record.code] : record.codes}/>
                {record.seqNum}
              </Group>
            )
          },
          {
            ...getColumnFilterProps(filterConfig, "resourceId"),
            title: t("table.entity.transportlineitem.column.resourceid", "Ticket ID"),
            accessor: "resourceId",
            width: 50,
            sortable: true,
          },
          {
            ...getColumnFilterProps(filterConfig, "ticketNumber"),
            title: t("table.entity.transportlineitem.column.ticketnumber", "Ticket Number"),
            accessor: "ticketNumber",
            width: 50,
            sortable: true,
          },
          {
            title: t("table.entity.transportlineitem.column.cost", "Cost"),
            accessor: "cost",
            width: 50,
            sortable: true,
            render: ({totalCost}) => (
              <FormattedCurrency value={totalCost}/>
            )
          },
          {
            title: t("table.entity.transportlineitem.column.description", "Description"),
            accessor: "pickupId",
            width: 200,
            sortable: true,
            render: (record) => (
              <TliDescriptionCell tli={record}/>
            ),
          },
          {
            title: '',
            accessor: 'actions',
            width: 25,
            render: (record) => {
              return (
                <Group spacing={4} position="right" noWrap>
                  <ActionIcon variant="transparent" onClick={() => setSelectedRecord(record)}>
                    <IconReportMoney size={14}/>
                  </ActionIcon>
                </Group>
              )
            }
          }
        ]}
      />
      {selectedRecord &&
        <CostDetailsModal
          title={"Cost Breakdown"}
          size="xl"
          opened={true}
          onClose={() => setSelectedRecord(undefined)}
          value={selectedRecord}/>
      }
    </>
  );
};
